import i18n from 'i18next'
import axiosInstance from '../axios/axiosInstance'
import store from '../redux/store'
import { ACCOUNT_SET_LANGUAGE_DONE } from '../redux/types'
import { detectInitialLanguage } from './detect'
import { reviewLanguageOverride } from './highlight'
import { setLanguage } from './index'
import {
  LanguageCode,
  NativeLanguageSource,
  PSEUDO_LOCALE,
  normalizeLanguage,
  profileLanguageFields,
  toNativeLanguageSource,
} from './languages'
import {
  clearLanguageNoticeDecision,
  languageNoticeAnswer,
  patchStoredUserLanguage,
  readLanguageNoticeDecision,
  rememberLanguageNoticeAnswer,
} from './languageNotice'

export interface LanguageSettings {
  native_language?: LanguageCode
  show_english_meanings?: boolean
  /** The web picked this language from the browser; the learner did not choose it. */
  detected?: boolean
}

interface SavedLanguageSettings {
  native_language?: LanguageCode
  native_language_source?: NativeLanguageSource
  show_english_meanings?: boolean
}

/**
 * `PUT profile/custom/language`. Updates the store with what the server saved (or, if it answers
 * without the fields, with what was sent). A language saved without `detected` counts as chosen.
 */
export const saveLanguageSettings = async ({ detected, ...settings }: LanguageSettings) => {
  const body = detected && settings.native_language !== undefined ? { ...settings, detected: true } : settings
  const response = await axiosInstance.put('profile/custom/language', body)
  const saved = response?.data?.customFields || response?.data || {}
  const payload: SavedLanguageSettings = {}
  if (settings.native_language !== undefined) {
    payload.native_language = profileLanguageFields(saved).native_language || settings.native_language
    payload.native_language_source =
      toNativeLanguageSource(saved.native_language_source) || (detected ? 'detected' : 'chosen')
  }
  if (settings.show_english_meanings !== undefined) {
    payload.show_english_meanings =
      typeof saved.show_english_meanings === 'boolean' ? saved.show_english_meanings : settings.show_english_meanings
  }
  store.dispatch({ type: ACCOUNT_SET_LANGUAGE_DONE, payload })
  patchStoredUserLanguage(payload)
  return payload
}

/**
 * The language setting: switches the interface at once and saves it to the profile. If the save
 * fails the interface goes back, since the next profile load would put it back anyway.
 */
export const saveLanguage = async (requested: string) => {
  const language = normalizeLanguage(requested)
  const previous = i18n.language
  await setLanguage(language)
  try {
    await saveLanguageSettings({ native_language: language })
  } catch (error) {
    if (previous && previous !== language) await setLanguage(previous)
    throw error
  }
  // A choice made here answers the "maayot is now in ..." notice too.
  clearLanguageNoticeDecision()
  return language
}

/**
 * An answer to the "maayot is now in ..." notice (Switch to English, Keep, or close). Never
 * blocks: the store marks the language chosen at once (so the notice is gone on every page), the
 * interface switches at once when the answer is another language, and the save runs in the
 * background. Until the server has it, this device remembers the answer
 * (`maayot.langNoticeDecision`), so the notice does not come back and the next profile load
 * retries the save. Resolves to whether the save went through.
 */
export const answerLanguageNotice = (memberId: string, requested: LanguageCode): Promise<boolean> => {
  const language = normalizeLanguage(requested)
  rememberLanguageNoticeAnswer(memberId, language)
  store.dispatch({
    type: ACCOUNT_SET_LANGUAGE_DONE,
    payload: { native_language: language, native_language_source: 'chosen' },
  })
  patchStoredUserLanguage({ native_language: language, native_language_source: 'chosen' })
  if (i18n.language !== language) setLanguage(language).catch(() => undefined)
  return saveLanguageSettings({ native_language: language }).then(
    () => {
      clearLanguageNoticeDecision()
      return true
    },
    () => false,
  )
}

let savedDetectedFor: string | null = null
let retriedDecisionFor: string | null = null

/** Test hook. */
export const resetProfileLanguageSync = () => {
  savedDetectedFor = null
  retriedDecisionFor = null
}

/**
 * An answer to the notice that this profile does not show yet: one this device could not save,
 * or one saved after this profile response left the server. Returns true when it decides the
 * language (the profile still holds the detected one). An unsaved answer is saved again, once per
 * session.
 */
const applyPendingNoticeAnswer = (memberId: string, customFields: any): boolean => {
  const pending = languageNoticeAnswer(memberId)
  if (!pending) return false
  const { native_language, native_language_source } = profileLanguageFields(customFields)
  if (native_language && native_language_source === 'chosen') {
    // The server has a choice: this answer, saved after all, or one made later elsewhere.
    clearLanguageNoticeDecision()
    return false
  }
  if (i18n.language !== pending) setLanguage(pending).catch(() => undefined)
  store.dispatch({ type: ACCOUNT_SET_LANGUAGE_DONE, payload: { native_language: pending, native_language_source: 'chosen' } })
  if (readLanguageNoticeDecision(memberId) && retriedDecisionFor !== memberId) {
    retriedDecisionFor = memberId
    saveLanguageSettings({ native_language: pending }).then(clearLanguageNoticeDecision, () => undefined)
  }
  return true
}

/**
 * Runs whenever a fresh profile or login response reaches the store. A learner who has chosen a
 * language gets it everywhere. A learner who has not gets the language this browser detected saved
 * to their profile, once per session, so emails and the apps follow it too.
 */
export const syncLanguageWithProfile = (credentials: { memberId?: string; customFields?: any } | null | undefined) => {
  if (!credentials?.memberId) return
  // The development pseudo-locale is a deliberate override: leave it alone. So is a page opened
  // from the translation review dashboard in a given language (highlight.ts).
  if (i18n.language === PSEUDO_LOCALE || reviewLanguageOverride()) return
  if (applyPendingNoticeAnswer(credentials.memberId, credentials.customFields)) return
  const { native_language } = profileLanguageFields(credentials.customFields)
  if (native_language) {
    if (i18n.language !== native_language) setLanguage(native_language).catch(() => undefined)
    return
  }
  if (savedDetectedFor === credentials.memberId) return
  savedDetectedFor = credentials.memberId
  const detected = normalizeLanguage(i18n.language || detectInitialLanguage())
  // One try per session; a failed save is tried again on the next visit. `detected` keeps it
  // apart from a language the learner chose, for the "maayot is now in ..." notice.
  saveLanguageSettings({ native_language: detected, detected: true }).catch(() => undefined)
}
